// src/hooks/useTax.js
import { useState, useEffect, useCallback } from "react";
import { taxService } from "../services/taxService";
import { toast } from "react-hot-toast";

/**
 * useTax Hook
 * Handles fetching, creating, updating, and deleting taxes
 */
export const useTax = () => {
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(false);

  /** 🔁 Mapper: Frontend camelCase → Backend camelCase (API payload) */
  const mapToApiFormat = (data = {}) => ({
    name: data.name || "",
    country: data.country || "",
    state: data.state || "",
    startDate: data.startDate || null,

    baseRate:
      data.baseRate !== "" && data.baseRate != null
        ? Number(data.baseRate)
        : 0,

    rate: data.rate !== "" && data.rate != null ? Number(data.rate) : 0,

    centerGstRate:
      data.centerGstRate !== "" && data.centerGstRate != null
        ? Number(data.centerGstRate)
        : null,

    stateGstRate:
      data.stateGstRate !== "" && data.stateGstRate != null
        ? Number(data.stateGstRate)
        : null,

    isActive: data.isActive ?? true,
    isDefault: data.isDefault ?? false,
  });

  /** 🔁 Mapper: Backend → Frontend camelCase */
  const mapFromApiFormat = (data = {}) => ({
    id: data.id,

    name: data.name ?? data.taxName ?? data.tax_name ?? "",
    rate: data.rate ?? 0,
    centerGstRate: data.centerGstRate ?? data.center_gst_rate ?? null,
    stateGstRate: data.stateGstRate ?? data.state_gst_rate ?? null,
    baseRate: data.baseRate ?? data.base_rate ?? null,
    startDate: data.startDate ?? data.start_date ?? "",
    country: data.country ?? "",
    state: data.state ?? "",

    isActive: data.isActive ?? true,
    isDefault: data.isDefault ?? false,

    // optional meta fields if backend sends them
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
    createdBy: data.createdBy ?? data.created_by,
    updatedBy: data.updatedBy ?? data.updated_by,
    branchId: data.branchId ?? null,
    branchName: data.branchName ?? null,
  });

  /** ✅ Fetch all taxes */
  const fetchTaxes = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const response = await taxService.getAll(filters);

      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
        ? response.data.data
        : [];

      if (!Array.isArray(data)) {
        console.warn("⚠️ Unexpected tax response format:", response);
        setTaxes([]);
        return;
      }

      const mappedData = data.map(mapFromApiFormat);
      setTaxes(mappedData);
    } catch (error) {
      console.error("❌ Failed to fetch tax:", error);
      toast.error("Failed to fetch tax");
      setTaxes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /** ✅ Create new tax */
  const createTax = async (data) => {
    try {
      const payload = mapToApiFormat(data);
      await taxService.create(payload);
      toast.success("Tax created successfully");
      await fetchTaxes();
    } catch (error) {
      console.error("❌ Failed to create tax:", error);
      toast.error("Failed to create tax");
      throw error;
    }
  };

  /** ✅ Update existing tax */
  const updateTax = async (id, data) => {
    try {
      const payload = mapToApiFormat(data);
      await taxService.update(id, payload);
      toast.success("Tax updated successfully");
      await fetchTaxes();
    } catch (error) {
      console.error(`❌ Failed to update tax ${id}:`, error);
      toast.error("Failed to update tax");
      throw error;
    }
  };

  /** ✅ Delete tax */
  const deleteTax = async (id) => {
    try {
      await taxService.delete(id);
      toast.success("Tax deleted");
      setTaxes((prev) => prev.filter((t) => t.id !== id));
    } catch (error) {
      console.error(`❌ Failed to delete tax ${id}:`, error);
      toast.error("Failed to delete tax");
      throw error;
    }
  };

  /** 🔁 Auto-fetch on mount */
  useEffect(() => {
    fetchTaxes();
  }, [fetchTaxes]);

  return {
    taxes,
    loading,
    fetchTaxes,
    createTax,
    updateTax,
    deleteTax,
  };
};

export default useTax;
